import type { ReactElement, ReactNode } from 'react';
import { AVATAR_ANIMAL_KEYS, AVATAR_SCENE_KEYS } from '../../../../../../shared/ember-avatars.js';
import { Link } from '../../components/Link';
import { PageBack } from '../../components/PageBack';
import { PresenceAvatar } from '../../components/PresenceAvatar';
import './new-friends.css';

/**
 * What the New friends pages share (2026-10-26 founder; edge friends.js): the bar, a face, the little drawings, and
 * how a clock and a day are said. A card's picture is a room avatar (an `animal:scene` combo, the same pool a room
 * seat draws from), never the person's own presence picture.
 */

/** The card's limits (edge friends.js FRIENDS). */
/**
 * A card's limits. TOPICS_TICKED: how many of Ember's drafted topics come ticked — the rest stand first among the chips,
 * unticked, so the card is not full before the person has picked anything (2026-10-26 walk-through: six drafted, six
 * ticked, every other chip greyed out and "Something else" gone).
 */
export const CARD = Object.freeze({ TOPICS_MAX: 6, TOPICS_TICKED: 3, TOPIC_MAX_CHARS: 30, LINE_MAX_CHARS: 160, NAME_MAX_CHARS: 24 });

/**
 * A line's field grown to its words, as a ref and on each keystroke: the line Ember drafted is read whole, not cut off
 * after three rows (2026-10-26 walk-through).
 */
export function fitHeight(el: HTMLTextAreaElement | null): void {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
}

/** A fresh picture for a card: one animal on one scene, from the room avatar pool. */
export function randomAvatar(): string {
  const pick = (xs: readonly string[]): string => xs[Math.floor(Math.random() * xs.length)];
  return `${pick(AVATAR_ANIMAL_KEYS)}:${pick(AVATAR_SCENE_KEYS)}`;
}

/** The first word of a name, cut to a card's name. */
export function firstName(name: string | null | undefined): string {
  return String(name || '').trim().split(/\s+/)[0]?.slice(0, CARD.NAME_MAX_CHARS) || '';
}

/** A clock as a place in the day ("Central time"), never the zone's id; null when the browser can't name it. */
export function zonePlace(zone: string | null | undefined): string | null {
  if (!zone) return null;
  try {
    const name = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'longGeneric' })
      .formatToParts(new Date()).find((p) => p.type === 'timeZoneName')?.value || '';
    if (!/ Time$/.test(name)) return null;
    return `${name.replace(/( Standard)? Time$/, '')} time`;
  } catch {
    return null;
  }
}

/** A day as the page says it: "today", "Nov 2", or "Nov 2, 2025" once it is not this year. */
export function dayLabel(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return 'today';
  return d.toLocaleDateString('en-US', d.getFullYear() === now.getFullYear()
    ? { month: 'short', day: 'numeric' }
    : { month: 'short', day: 'numeric', year: 'numeric' });
}

/** A face on a ring (the ring's colour is the class's). */
export function Face({ avatar, size, className = '' }: { avatar: string; size: number; className?: string }): ReactElement {
  return (
    <span className={`nf-face${className ? ` ${className}` : ''}`} style={{ width: size, height: size }} aria-hidden>
      <PresenceAvatar avatarKey={avatar} size={size} />
    </span>
  );
}

/** The page's bar: Back (or the given control) on the left, something in the middle. */
export function FriendsBar({ to = '/friends', left, children }: { to?: string; left?: ReactNode; children?: ReactNode }): ReactElement {
  return (
    <header className="nf-bar">
      {left ?? <PageBack to={to} className="nf-back" />}
      <div className="nf-bar-mid">{children}</div>
      <span className="nf-bar-end" aria-hidden />
    </header>
  );
}

/** Someone who may not take part sees this and nothing of the feature (2026-10-26 founder). */
export function NotAvailable(): ReactElement {
  return (
    <main className="nf-page" data-log-seen="friends_unavailable">
      <FriendsBar to="/" />
      <div className="nf-quiet">
        <p>This page isn’t available right now.</p>
        <Link to="/" className="ember-btn ember-btn-secondary" data-log="friends_unavailable_home">Go home</Link>
      </div>
    </main>
  );
}

export const FLAME = (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
    <path d="M12 2.5c1.2 3.2 5.5 5.6 5.5 10.6A5.5 5.5 0 0 1 6.5 13c0-2.3 1.1-3.7 2.3-5 .3 1.6 1.1 2.7 2.2 3.2C10.6 8.6 10.9 5.2 12 2.5z" fill="#F4720C" />
    <path d="M12 12.2c.8 1.4 2.4 2.3 2.4 4.2a2.4 2.4 0 0 1-4.8 0c0-1.5 1-2.6 2.4-4.2z" fill="#FFB010" />
  </svg>
);

export function Sparkle({ size, color = '#FFB010', className = '' }: { size: number; color?: string; className?: string }): ReactElement {
  return (
    <svg className={className} width={size} height={size} viewBox="0 0 24 24" aria-hidden>
      <path d="M12 2l2.2 7.8L22 12l-7.8 2.2L12 22l-2.2-7.8L2 12l7.8-2.2z" fill={color} />
    </svg>
  );
}

/** Someone not here yet: a dashed person. */
export function DashedPerson({ size, color }: { size: number; color: string }): ReactElement {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
      <circle cx="12" cy="9" r="4" stroke={color} strokeWidth="1.8" fill="none" strokeDasharray="2 2.4" />
      <path d="M5 20c1-3.6 3.8-5.6 7-5.6s6 2 7 5.6" stroke={color} strokeWidth="1.8" fill="none" strokeLinecap="round" strokeDasharray="2 2.4" />
    </svg>
  );
}

export const CHEVRON = (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
    <path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const BACK_CHEVRON = (
  <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden>
    <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="1.9" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** Binoculars, for Ember looking. */
export function Binoculars({ width, className = '', legs = false }: { width: number; className?: string; legs?: boolean }): ReactElement {
  return (
    <svg className={className} width={width} height={Math.round(width * 50 / 70)} viewBox="0 0 70 50" aria-hidden>
      <rect x="4" y="16" width="40" height="16" rx="6" fill="#FFFDF8" stroke="#B94B0A" strokeWidth="3" />
      <rect x="42" y="12" width="20" height="24" rx="6" fill="#FFFDF8" stroke="#B94B0A" strokeWidth="3" />
      {legs && <path d="M18 32l-6 14M28 32l4 14" stroke="#B94B0A" strokeWidth="2.2" strokeLinecap="round" />}
    </svg>
  );
}

/** Chip tints, in turn. */
export const TINTS = ['#FBE1CF', '#E3ECE0', '#ECE6F6', '#FFF1C7', '#E2EEF6'];
/** A little tilt, in turn. */
export const TILTS = ['-2deg', '1.5deg', '-1deg', '2deg'];
